import { z } from "zod/v4-mini"
import { type Control } from "react-hook-form"
import { zfd } from "zod-form-data"

import { CustomerReturnStatus, CustomerTransactionType, ImageEntity, PaymentMethod, ProductType, ShiftStatus, Status } from "../../db2/src/enums"

export const listProductsInput = z.object({
  // type: z.optional(z.enum(ProductType)),
  types: z.optional(z.array(z.enum(ProductType))),
  limit: z._default(z.optional(z.number()), 100),
  collectionId: z.optional(z.number()),
  // cursor: z.optional(z.number()),
  cursor: z.optional(z.object({ id: z.number(), rank: z.number(), updatedAt: z.optional(z.number()) })),
  brandId: z.optional(z.number()),
  search: z.optional(z.string()),
  //
  onlyBaseProducts: z.optional(z.boolean()),
  inventoryCollectionIds: z.optional(z.array(z.number())),
  status: z._default(z.optional(z.array(z.enum(Status))), ["active"]),
})

export const listProductsByStoragePlaceInput = z.object({
  ...listProductsInput.shape,
  storagePlaceId: z.number(),
  sortByLatestUpdate: z.optional(z.boolean()),
})

export const listDenormalizedProductsInput = z.object({
  storagePlaceId: z.number(),
})

export const normalizeProductInput = z.object({
  storagePlaceId: z.number(),
  baseProductId: z.number(),
})

export const getProductInput = z.union([
  z.object({ id: z.number() }), //
])

export const getProductMultiple = z.object({
  ids: z.array(z.number()),
})

export const editMenuSettingsInput = z.object({
  id: z.number(),
  listedInApp: z.optional(z.boolean()),
  isAvailable: z.optional(z.boolean()),
  canExceedInventory: z.optional(z.boolean()),
  maxOrderQuantity: z.optional(z.number().check(z.int(), z.minimum(1))),
  price: z.optional(z.number().check(z.int(), z.minimum(0))),
  originalPrice: z.optional(z.number().check(z.int(), z.minimum(0))),
})

export const _productFormInput = z.object({
  id: z.optional(z.number()),
  name: z.string().check(z.minLength(3)),
  description: z.optional(z.string()),
  type: z.enum(["STANDARD_PRODUCT", "LOCAL_PRODUCT", "WEIGHT_PRODUCT"]),
  upc: z.string(),
  baseUpc: z.optional(z.string()),
  cost: z.optional(z.number().check(z.int(), z.minimum(0))),
  price: z.number().check(z.int(), z.minimum(0)),
  originalPrice: z.optional(z.number().check(z.int(), z.minimum(0))),
  value: z.optional(z.number().check(z.int(), z.positive())),
  brandId: z.optional(z.number()),

  //
  inventoryCollections: z.optional(z.array(z.number())),

  // Menu-related fields (optional)
  listedInApp: z.optional(z.boolean()),
  isAvailable: z.optional(z.boolean()),
  canExceedInventory: z.optional(z.boolean()),
  maxOrderQuantity: z.optional(z.number().check(z.int(), z.minimum(1))),
})

export const productFormInput = _productFormInput //
  .check(z.refine((value) => value.upc !== value.baseUpc, "UPC and Base UPC cannot be the same"))

export const productEditFormInput = z.extend(_productFormInput, { id: z.number() }).check(z.refine((value) => value.upc !== value.baseUpc, "UPC and Base UPC cannot be the same"))

// transaction
export const productFormSchema = z.object({
  sku: z.string().check(z.minLength(2, "SKU is required")),
  quantity: z.coerce.number<number>().check(z.positive()),
  cost: z.coerce.number<number>().check(z.positive()),
  suggestedPrice: z.coerce.number<number>().check(z.positive()),
})

// "PURCHASE" // from supplier
// "SALE"  // to customer
// "RETURN" // to supplier
// "TRANSFER" // between storage places

// ["PURCHASE", "SALE", "RETURN", "TRANSFER"]
const purchaseTransactionBase = {
  supplierId: z.number("Supplier is required"),
  storagePlaceId: z._default(z.number("Storage place is required"), 0),
  products: z.array(productFormSchema).check(z.minLength(1, "At least one product is required")),
  discount: z.optional(z.number().check(z.minimum(0))),
  notes: z.optional(z.string()),
  createdAt: z.optional(z.number()),
}

export const transactionListInput = z.object({
  fromDate: z.optional(z.number()),
  toDate: z.optional(z.number()),
  supplierId: z.optional(z.number()),
  status: z.optional(z.enum(["active", "cancelled"])),
  cursor: z.optional(z.number()),
  limit: z.optional(z.number()),
})

export const TransactionTypeEnum = ["purchase", "return"] as const

export const purchaseTransactionFormSchema = z.object({
  type: z._default(z.enum(TransactionTypeEnum), "purchase"),
  ...purchaseTransactionBase,
})

export interface TransactionFormProps {
  id?: number
}

export type TransactionFormControl = Control<z.infer<typeof purchaseTransactionFormSchema>>

export const EMPLOYEE_ROLES = ["cashier", "worker", "admin", "employee"] as const

export const createEmployeeInput = z.object({
  name: z.string().check(z.minLength(1, "Name is required")),
  username: z.string().check(z.minLength(1, "Username is required")),
  password: z.string().check(z.minLength(6, "Password must be at least 6 characters")),
  role: z.enum(EMPLOYEE_ROLES),
})

export const updateEmployeeInput = z.extend(createEmployeeInput, {
  id: z.number(),
})

export const getEmployeeInput = z.object({
  id: z.number(),
})

export const deleteEmployeeInput = z.object({
  id: z.number(),
})

export const addEmployeeBalanceInput = z.object({
  employeeId: z.number(),
  amount: z.number().check(z.positive()),
  notes: z.optional(z.string()),
  createdAt: z.optional(z.number()),
})

export const employeePayment = z.object({
  employeeId: z.number(),
  amount: z.number().check(z.positive()),
  notes: z.optional(z.string()),
  createdAt: z.optional(z.number()),
})

// shifts

export const listShiftInput = z.object({
  cursor: z.optional(z.object({ id: z.number() })),
  limit: z._default(z.number(), 50),
  from: z.optional(z.number()),
  to: z.optional(z.number()),
  status: z.optional(z.enum(ShiftStatus)),
})

export const createShiftInput = z.object({
  employeeId: z.number(),
  startTime: z.number(),
  endTime: z.number(),
  cash: z.number(),
  visa: z.number(),
  wallet: z.number(),
})

export const updateShiftInput = z.extend(createShiftInput, {
  id: z.number(),
})

export const getShiftInput = z.object({
  id: z.number(),
})

export const deleteShiftInput = z.object({
  id: z.number(),
})

export const confirmShiftInput = z.object({
  id: z.number(),
  actualCash: z.number().check(z.minimum(0)),
  actualVisa: z.number().check(z.minimum(0)),
  actualWallet: z.number().check(z.minimum(0)),
  confirmationNote: z.optional(z.string()),
})

// range to filter shifts by
export const shiftRangeInput = z.object({
  from: z.optional(z.number()),
  to: z.optional(z.number()),
})

// expenses

export const createExpenseInput = z.object({
  amount: z.number().check(z.gt(0)),
  category: z.number(),
  balanceType: z.enum(["cash", "visa", "wallet"]),
  date: z.optional(z.number()),
  notes: z.optional(z.string()),
})

export const createExpenseCategoryInput = z.object({
  name: z.string().check(z.trim(), z.minLength(1), z.maxLength(60)),
  color: z.optional(z.string().check(z.regex(/^#[0-9A-Fa-f]{6}$/))),
})

// supplier payments
export const createSupplierPaymentInput = z.object({
  supplierId: z.number(),
  amount: z.number().check(z.gt(0)),
  balanceType: z.enum(["cash", "visa", "wallet"]),
  notes: z.optional(z.string()),
  date: z.number(),
})

export const listSupplierPaymentsInput = z.object({
  supplierId: z.optional(z.number()),
  startDate: z.optional(z.number()),
  endDate: z.optional(z.number()),
  cursor: z.optional(z.object({ date: z.number(), id: z.number() })),
  limit: z.optional(z.number()),
})

// trasfer cash
export const transferFromWalletInput = z.object({
  // amount got out of our balance
  outAmount: z.number().check(z.gt(0)),
  // amount got into our balance
  inAmount: z.number().check(z.gt(0)),
  // which balance type
  balanceType: z.enum(["cash", "visa"]),
  notes: z.optional(z.string()),
  createdAt: z.optional(z.number()),
})

export const transferToWalletInput = z.omit(transferFromWalletInput, { balanceType: true })

export const manualTransactionInput = z.object({
  type: z.enum(["deposit", "withdraw"]),
  amount: z.number().check(z.gt(0)),
  balanceType: z.enum(["cash", "visa", "wallet"]),
  notes: z.optional(z.string()),
  date: z.optional(z.number()),
})

// login
export const loginInput = z.object({
  username: z.string().check(z.includes("@")),
  password: z.string().check(z.minLength(4)),
})

// customers

export const listCustomersInput = z.object({
  filter: z.optional(
    z.object({
      phone: z.optional(z.string()),
      name: z.optional(z.string()),
    }),
  ),
  cursor: z.optional(z.number()),
  limit: z.optional(z.number()),
})

export const createCustomerInput = z.object({
  name: z.string(),
  phoneNumbers: z.optional(
    z.array(
      z.object({
        id: z.optional(z.number()),
        phone: z.string().check(z.startsWith("01"), z.length(11)),
        primary: z.boolean(),
      }),
    ),
  ),
})

export const editCustomerInput = z.extend(createCustomerInput, { id: z.number() })

export const createSupplierInput = z.object({
  name: z.string().check(z.minLength(1, "Name is required")),
  phones: z.optional(z.array(z.object({ phone: z.string() }))),
  notes: z.optional(z.string()),
  brandIds: z.optional(z.array(z.number())),
})

export const editSupplierInput = z.extend(createSupplierInput, { id: z.number() })

// --- Notifications ---
// Discriminated union on `audience` so invalid combinations are impossible.
// `registered` targets devices linked to a customer, `unregistered` targets
// guest devices (customerId NULL), `all` targets both, `group` targets the
// customers of an admin-managed notification group. The customer app has no
// notification-tap routing, so the message is just title + body (no raw data
// JSON, no priority).

// Shared message constraints — used by both the API schema and the dashboard
// form (via zodResolver) so validation cannot drift between the two.
export const notificationMessageInput = z.object({
  title: z.string().check(z.minLength(1), z.maxLength(100)),
  body: z.string().check(z.minLength(1), z.maxLength(500)),
})

const customerAudience = z.object({
  audience: z.literal("customer"),
  customerId: z.number(),
})

const allAudience = z.object({ audience: z.literal("all") })

const registeredAudience = z.object({ audience: z.literal("registered") })

const unregisteredAudience = z.object({ audience: z.literal("unregistered") })

const groupAudience = z.object({
  audience: z.literal("group"),
  groupId: z.number(),
})

export const previewAudienceInput = z.discriminatedUnion("audience", [
  customerAudience,
  allAudience,
  registeredAudience,
  unregisteredAudience,
  groupAudience,
])

export const sendNotificationInput = z.discriminatedUnion("audience", [
  z.object({ ...customerAudience.shape, ...notificationMessageInput.shape }),
  z.object({ ...allAudience.shape, ...notificationMessageInput.shape }),
  z.object({ ...registeredAudience.shape, ...notificationMessageInput.shape }),
  z.object({ ...unregisteredAudience.shape, ...notificationMessageInput.shape }),
  z.object({ ...groupAudience.shape, ...notificationMessageInput.shape }),
])

export const listNotificationHistoryInput = z.object({
  cursor: z.optional(z.number()),
  limit: z.optional(z.number()),
})

// --- Notification groups ---
// Groups are admin-managed audience segments; the global opt-out always wins.

export const createNotificationGroupInput = z.object({
  name: z.string().check(z.minLength(1), z.maxLength(100)),
})

export const updateNotificationGroupInput = z.object({
  id: z.number(),
  name: z.string().check(z.minLength(1), z.maxLength(100)),
})

export const deleteNotificationGroupInput = z.object({ id: z.number() })

export const notificationGroupMembersInput = z.object({
  groupId: z.number(),
  customerIds: z.array(z.number()).check(z.minLength(1)),
})

export const listCustomerTransactionsInput = z.object({
  id: z.number(),
  cursor: z.optional(z.number()),
  limit: z.optional(z.number()),
})

export const customerTransactionInput = z.object({
  type: z.enum(CustomerTransactionType),
  customerId: z.number(),
  amount: z.number(),
  note: z.optional(z.string()),
  date: z.optional(z.number()),
  method: z._default(z.enum(PaymentMethod), PaymentMethod.cash),
  receiptRef: z.optional(z.string()),
})

// product inventory movement
export const singleProductMovementInput = z.object({
  storagePlaceId: z.number(),
  productIds: z._default(z.array(z.number()), []),
  from: z.number(), // unix seconds
  to: z.number(), // unix seconds
})

export const trackProductInput = z.object({
  productIds: z.array(z.number()),
  storagePlaceId: z.optional(z.number()),
  from: z.optional(z.number()), // utc unix
  to: z.optional(z.number()), // utc unix
  limit: z.optional(z._default(z.number(), 50)),
  cursor: z.optional(z.number()), // For pagination (movement ID)
})

// Inventory movement inputs
export const MovementTypeEnum = ["SALE", "RESTOCK", "ADJUSTMENT", "RETURN"] as const

export const adjustStockInput = z.object({
  productId: z.number(),
  storagePlaceId: z.number(),
  newQuantity: z.coerce.number<number>().check(z.minimum(0)),
  reason: z.optional(z.string()),
})

export const listAdjustmentsInput = z.object({
  from: z.optional(z.number()), // utc unix
  to: z.optional(z.number()), // utc unix
  limit: z.optional(z._default(z.number(), 25)),
  cursor: z.optional(z.number()), // movement id (descending keyset)
})

export const updateLowStockThresholdInput = z.object({
  productId: z.number(),
  storagePlaceId: z.number(),
  lowStockThreshold: z.nullable(z.number().check(z.minimum(0))),
})

export const inventoryReportInput = z.object({
  storagePlaceId: z.number(),
  from: z.number(),
  to: z.number(),
  collectionId: z.optional(z.number()),
  productId: z.optional(z.number()),
  productIds: z.optional(z.array(z.number()).check(z.maxLength(200))),
})

// Weight Defined Product Modal
export const weightDefinedProductInput = z.object({
  baseProductId: z.number(),
  name: z.string().check(z.minLength(3)),
  weight: z.number().check(z.int(), z.minimum(1), z.maximum(99999)), // grams
  price: z.number().check(z.int(), z.minimum(0)),
  originalPrice: z.optional(z.number().check(z.int(), z.minimum(0))),
})

export const createWeightDefinedInput = z.object({
  baseProductId: z.number(),
  weight: z.number().check(z.int(), z.gt(0)),
  status: z.optional(z.enum(["active", "inactive"])),
})

// Scale Product Form (WEIGHT_PRODUCT)
export const scaleProductFormInput = z.object({
  name: z.string().check(z.minLength(3)),
  description: z.optional(z.string()),
  code: z.number().check(z.int(), z.minimum(1), z.maximum(99999)), // PLU code 1-99999, converts to UPC 20XXXXX
  brandId: z.optional(z.number()),
  price: z.number().check(z.int(), z.minimum(0)), // price per kg in milli-units
  originalPrice: z.optional(z.number().check(z.int(), z.minimum(0))),
})

// sales details — z.union for mutually exclusive modes (same pattern as pos.ts Event payload)
const _salesDetailsShiftMode = z.object({
  shiftId: z.number(),
  cursor: z.optional(z.number()),
})

const _salesDetailsDateRangeMode = z
  .object({
    from: z.number(),
    to: z.number(),
    cursor: z.optional(z.number()),
  })
  .check(z.refine((val) => val.to >= val.from, "to must be >= from"))

export const salesDetailsInput = z.union([_salesDetailsShiftMode, _salesDetailsDateRangeMode])

export { CustomerTransactionType, ImageEntity, ProductType, PaymentMethod }

// images
export const imageInput = zfd.formData({
  thumbhash: z.optional(z.string()),
  entity: z.enum(ImageEntity),
  entityId: z.coerce.number<number | string>(),
  image: z.pipe(
    z.instanceof(File),
    z.custom<File>((file) => {
      if (file.size > 262_144) return "Image must be less than 256KB"
      if (!file.type.startsWith("image/")) return "Invalid file type — must be an image"
      return true
    }),
  ),
})

export const uploadImageBySku = z.object({
  thumbhash: z.string(),
  sku: z.string(),
  imageUrl: z.url(),
  brand: z.string(),
})

export const returnItemInput = z.object({
  productId: z.number(),
  upc: z.string(),
  returnQuantity: z.number().check(z.minimum(1)),
  returnWeight: z.optional(z.number()),
})

export const processReturnInput = z.object({
  receiptId: z.optional(z.number()),
  orderId: z.optional(z.number()),
  items: z.array(returnItemInput).check(z.minLength(1)),
  refundMethod: z._default(z.enum(PaymentMethod), "cash"),
  reason: z.string().check(z.minLength(3)),
  customerId: z.optional(z.number()),
})

export const returnListInput = z.object({
  status: z.optional(z.enum(CustomerReturnStatus)),
  from: z.optional(z.number()),
  to: z.optional(z.number()),
  cursor: z.optional(z.number()),
  limit: z.optional(z.number()),
})
